import { useCallback, memo } from 'react';
import { Pagination } from './Pagination';

export interface EntityGalleryProps<T> {
  data: T[];
  getRowKey: (item: T) => string | number;
  renderTile: (item: T, index: number) => React.ReactNode;
  emptyMessage?: string;
  pageSize?: number;
  onTileClick?: (item: T, index: number) => void;
  loadingMore?: boolean;
}

function EntityGalleryComponent<T>({
  data,
  getRowKey,
  renderTile,
  emptyMessage = 'No items found',
  pageSize = 12,
  onTileClick,
  loadingMore = false,
}: EntityGalleryProps<T>) {
  const handleTileClick = useCallback(
    (item: T, index: number) => {
      onTileClick?.(item, index);
    },
    [onTileClick]
  );

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <p className="text-lg">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <Pagination data={data} pageSize={pageSize} loadingMore={loadingMore} getItemKey={getRowKey}>
      {(paginatedData) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedData.map((item, index) => (
            <div
              key={getRowKey(item)}
              onClick={() => handleTileClick(item, index)}
              className={onTileClick ? 'cursor-pointer' : ''}
            >
              {renderTile(item, index)}
            </div>
          ))}
        </div>
      )}
    </Pagination>
  );
}

export const EntityGallery = memo(EntityGalleryComponent) as typeof EntityGalleryComponent;
